import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import {
  loginDev,
  loginWithWidget,
  type AdminAuthErrorCode,
  type TelegramWidgetUser,
} from '@/lib/api';
import { ThemeSwitch } from '@/components/ThemeSwitch';
import { texts } from '@/lib/texts';
import { trpc } from '@/lib/trpc';

declare global {
  interface Window {
    onTelegramAuth?: (user: TelegramWidgetUser) => void;
  }
}

const errorText: Record<AdminAuthErrorCode, string> = {
  forbidden: texts.auth.errorForbidden,
  bad_signature: texts.auth.errorBadSignature,
  not_configured: texts.auth.errorNotConfigured,
  not_found: texts.auth.errorForbidden,
  network: texts.auth.errorNetwork,
};

function isErrorCode(v: string | null): v is AdminAuthErrorCode {
  return v !== null && v in errorText;
}

/** Виджет Telegram Login в callback-режиме: `data-onauth` → POST /admin-auth/telegram. */
function TelegramLoginWidget({
  botUsername,
  onAuth,
}: {
  botUsername: string;
  onAuth: (u: TelegramWidgetUser) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.onTelegramAuth = onAuth;
    const el = ref.current;
    if (!el) return;
    el.innerHTML = '';
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.async = true;
    script.setAttribute('data-telegram-login', botUsername);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-userpic', 'false');
    script.setAttribute('data-onauth', 'onTelegramAuth(user)');
    script.setAttribute('data-request-access', 'write');
    el.appendChild(script);
    return () => {
      delete window.onTelegramAuth;
      el.innerHTML = '';
    };
  }, [botUsername, onAuth]);

  return <div ref={ref} className="flex min-h-12 justify-center" />;
}

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const cfg = trpc.auth.config.useQuery();
  const [error, setError] = useState<AdminAuthErrorCode | null>(() => {
    const e = params.get('error');
    return isErrorCode(e) ? e : null;
  });
  const [busy, setBusy] = useState(false);
  const [devTgId, setDevTgId] = useState('');

  const finish = (result: Awaited<ReturnType<typeof loginWithWidget>>) => {
    setBusy(false);
    if (result.ok) {
      void navigate('/', { replace: true });
    } else {
      setError(result.error);
    }
  };

  const onWidgetAuth = (user: TelegramWidgetUser) => {
    setBusy(true);
    setError(null);
    void loginWithWidget(user).then(finish);
  };

  const onDevSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!/^\d+$/.test(devTgId)) return;
    setBusy(true);
    setError(null);
    void loginDev(devTgId).then(finish);
  };

  return (
    <main className="flex min-h-screen items-center justify-center px-6">
      <div className="w-full max-w-md rounded-2xl border border-border bg-surface p-8 shadow-sm">
        <div className="mb-6 flex justify-end">
          <ThemeSwitch className="w-48" />
        </div>
        <h1 className="text-2xl font-bold tracking-tight">{texts.auth.loginTitle}</h1>
        <p className="mt-2 text-sm text-muted">{texts.auth.loginBody}</p>

        <div className="mt-6">
          {cfg.isPending ? (
            <p className="text-sm text-muted">{texts.common.loading}</p>
          ) : cfg.isError ? (
            <p className="text-sm text-danger">{texts.auth.errorNetwork}</p>
          ) : cfg.data.botUsername ? (
            <TelegramLoginWidget botUsername={cfg.data.botUsername} onAuth={onWidgetAuth} />
          ) : (
            <p className="text-sm text-warning">{texts.auth.widgetUnavailable}</p>
          )}
        </div>

        {busy ? <p className="mt-4 text-sm text-muted">{texts.auth.checking}</p> : null}
        {error ? (
          <p role="alert" className="mt-4 rounded-lg bg-danger-bg px-3 py-2 text-sm text-danger">
            {errorText[error]}
          </p>
        ) : null}

        {cfg.data?.devLoginEnabled ? (
          <form onSubmit={onDevSubmit} className="mt-8 border-t border-dashed border-border pt-6">
            <h2 className="text-sm font-semibold">{texts.auth.devTitle}</h2>
            <p className="mt-1 text-xs text-muted">{texts.auth.devBody}</p>
            <div className="mt-3 flex gap-2">
              <input
                value={devTgId}
                onChange={(e) => setDevTgId(e.target.value.trim())}
                inputMode="numeric"
                placeholder={texts.auth.devTgId}
                className="focus:border-accent min-w-0 flex-1 rounded-lg border border-input px-3 py-2 text-sm outline-none"
              />
              <button
                type="submit"
                disabled={busy || !/^\d+$/.test(devTgId)}
                className="bg-accent rounded-lg px-4 py-2 text-sm font-semibold text-fg disabled:opacity-50"
              >
                {texts.auth.devSubmit}
              </button>
            </div>
          </form>
        ) : null}
      </div>
    </main>
  );
}
